/**
 * Extração: disparar uma coleta e acompanhar o que ela está fazendo.
 *
 * É a porta de operação do módulo documentado em `docs/extracao.md`. A outra
 * porta é a linha de comando (`uv run nectuno-coleta`); as duas chamam o mesmo
 * runner, e o estado das duas vive em `coleta_run` — não na memória do
 * servidor. Por isso esta tela mostra também a coleta que alguém disparou pelo
 * terminal, e sobrevive a um `--reload` da API no meio.
 *
 * Três coisas aqui são deliberadamente diferentes do que uma tela de progresso
 * costuma fazer, e todas pelo mesmo motivo — não mentir sobre o que se sabe:
 *
 * 1. NÃO HÁ PORCENTAGEM. Uma coleta não sabe quantas páginas existem antes de
 *    chegar à última. A barra é indeterminada; o número que anda é a página.
 * 2. O CONTADOR FICA EM ZERO ATÉ O FIM. `registros_vistos` só é gravado no
 *    `encerra()`. Mostrar "0 vistos" seria verdade e pareceria travamento, por
 *    isso enquanto corre a tela mostra a página, e o dito está embaixo.
 * 3. `parcial` NÃO VIRA "concluída" AQUI. Na navegação ele aparece traduzido;
 *    nesta tela, que existe para operar a coleta, a diferença entre "não havia
 *    licitação" e "não conseguimos ver" é justamente o que se veio ver.
 *
 * O DESENHO (20/10, "reimaginar tudo"): a nova coleta e a coleta em curso
 * lado a lado, no alto; as explicações longas recolhidas em "Como funciona";
 * e as últimas coletas como linhas (`ListaDeRodadas`), no lugar da tabela de
 * onze colunas.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): os cartões, o seletor do portal, o campo
 * do limite, os interruptores, o botão, a pílula, a dobra, a seção, a nota, o
 * ícone do relógio e os avisos saem de `src/ds`. Em `gestao.module.css` ficou
 * o que é desta tela: as duas colunas, o formulário e a coleta em curso (o
 * pulso e a barra que vai e vem).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../api/cliente";
import { AreaConfig } from "../componentes/AreaConfig";
import {
  Aviso, Botao, Campo, Cartao, Dobra, IconeRelogio, Interruptor, Nota, Pilula, Secao, Seletor,
} from "../ds";
import { useSessao } from "../sessao";
import { duracao, ListaDeRodadas } from "./ListaDeRodadas";
import css from "./gestao.module.css";

const TODAS = "";

export function Extracao() {
  const { podeOperar } = useSessao();
  const cliente = useQueryClient();
  const [fonte, setFonte] = useState(TODAS);
  const [limite, setLimite] = useState("");
  const [seco, setSeco] = useState(false);
  const [retoma, setRetoma] = useState(false);

  const fontes = useQuery({
    queryKey: ["fontes"],
    queryFn: api.fontes,
    staleTime: Infinity,
  });

  // A mesma chave que a Moldura usa: uma consulta só alimenta as duas, e o
  // aviso da navegação não fica um passo atrás do que esta tela mostra.
  const atual = useQuery({
    queryKey: ["rodada-atual"],
    queryFn: api.rodadaAtual,
    refetchInterval: (q) => (q.state.data ? 2000 : 15000),
  });
  const correndo = atual.data?.estado === "rodando" || atual.data?.estado === "na_fila";

  const rodadas = useQuery({
    queryKey: ["rodadas"],
    queryFn: () => api.rodadas(20),
    refetchInterval: correndo ? 5000 : false,
  });

  // Termo ativo é pré-requisito, não detalhe: `carrega([])` chama `sys.exit`, e
  // uma coleta com vocabulário vazio morre antes da primeira página.
  const termos = useQuery({
    queryKey: ["termos"],
    queryFn: () => api.termos(),
  });
  const ativos = termos.data?.filter((t) => t.estado === "ativo").length ?? null;

  const disparar = useMutation({
    mutationFn: () =>
      api.iniciarRodada(fonte, {
        ...(limite ? { limite: Number(limite) } : {}),
        seco,
        retoma,
      }),
    // Nada de `setQueryData` com a resposta: quando o runner demora mais que
    // 10 s para abrir o run, ela vem com id "0". Reconsultar traz a verdade.
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ["rodada-atual"] });
      void cliente.invalidateQueries({ queryKey: ["rodadas"] });
    },
  });

  // `iniciado_em` não está em Progresso, mas está na lista de rodadas — e a
  // linha da coleta em curso já vem nela.
  const linhaAtual = rodadas.data?.find((r) => r.id === atual.data?.rodada_id);
  const travado = !podeOperar || correndo;

  return (
    <AreaConfig
      titulo="Buscas nos portais"
      descricao="O sistema procura nos portais públicos as licitações que têm alguma das palavras de busca, e guarda só essas. O que não tem nenhuma palavra nunca entra na base."
    >
      {ativos === 0 && (
        <Aviso tom="erro" simples>
          Nenhuma palavra de busca ativa: sem ela, a busca não roda.{" "}
          <Link to="/vocabulario">Ativar palavras</Link>.
        </Aviso>
      )}

      <div className={css.duas}>
        {/* ── a nova coleta ──────────────────────────────────────────── */}
        <Cartao
          titulo="Nova busca"
          acoes={ativos !== null && <span className={css.mudo}>{ativos} palavras de busca ativas</span>}
        >
          <div className={css.formulario}>
            {!podeOperar && (
              <Aviso simples>
                Seu acesso é de leitura. Disparar é restrito a operadores; o
                acompanhamento é visível para todos.
              </Aviso>
            )}

            <div className={css.camposLado}>
              <Seletor rotulo="Portal" valor={fonte} aoMudar={setFonte} disabled={travado}
                       opcoes={[
                         { valor: TODAS, rotulo: "Todos os portais" },
                         ...(fontes.data ?? []).map((f) => ({ valor: f, rotulo: f })),
                       ]} />
              <Campo rotulo="Parar depois de" type="number" min={1} placeholder="sem limite"
                     aria-label="Parar depois de quantas licitações"
                     value={limite} onChange={(e) => setLimite(e.currentTarget.value)}
                     disabled={travado || retoma} />
            </div>

            {/* Excludentes: juntos, reabririam um run parcial de verdade e o
                fechariam como vazio. O servidor recusa com 422. */}
            <Interruptor rotulo="Simulação" descricao="percorre o portal e não guarda nada"
                         checked={seco} onChange={(e) => setSeco(e.currentTarget.checked)}
                         disabled={travado || retoma} />
            <Interruptor rotulo="Continuar a interrompida"
                         descricao="retoma a última busca incompleta (só PNCP)"
                         checked={retoma} onChange={(e) => setRetoma(e.currentTarget.checked)}
                         disabled={travado || seco || !!limite} />

            <div className={css.formularioPe}>
              <Botao variante="primario" disabled={travado || disparar.isPending}
                     onClick={() => disparar.mutate()}>
                {disparar.isPending ? "Iniciando…" : "Iniciar busca"}
              </Botao>
              {correndo && <span className={css.mudo}>Uma busca por vez.</span>}
            </div>

            {disparar.isError && <Aviso tom="erro" simples>{(disparar.error as Error).message}</Aviso>}
          </div>
        </Cartao>

        {/* ── a coleta em curso ─────────────────────────────────────── */}
        <Cartao titulo="Busca em andamento"
                acoes={atual.data && <Pilula tom="andamento">Em andamento</Pilula>}>
          {!atual.data ? (
            <div className={css.vazioCurso}>
              <IconeRelogio tamanho={28} strokeWidth={1} />
              <p>
                Nenhuma busca rodando agora. Ela roda todas as noites e quando
                alguém manda, por aqui.
              </p>
            </div>
          ) : (
            <div className={css.curso} aria-live="polite">
              <p className={css.cursoFonte}>
                <span className={css.pulso} aria-hidden="true" />
                <b>{atual.data.termo}</b>
                {linhaAtual && <span className={css.mudo}>há {duracao(linhaAtual.iniciada_em)}</span>}
              </p>
              <p className={css.cursoDetalhe}>{atual.data.detalhe}</p>
              {/* Indeterminada de propósito: o conector descobre o total ao
                  chegar no fim. Uma porcentagem inventada anda para trás. */}
              <div className={css.trilho} role="progressbar"
                   aria-label={`Coletando de ${atual.data.termo}`}>
                <i className={css.indeterminado} />
              </div>
              <p className={css.cursoRodape}>
                {atual.data.titulo} · página {atual.data.pagina || "—"}. Os
                totais só aparecem no fim; até lá, a página é o que anda.
              </p>
            </div>
          )}
        </Cartao>
      </div>

      <Dobra titulo="Como funciona" resumo="o limite, a simulação, continuar e a busca incompleta">
        <Nota>
          Sem escolher o portal, a busca passa por todos, um depois do outro; o
          que falha não derruba os outros. <b>Parar depois de</b> encerra a busca
          nas primeiras N licitações — serve para testar um portal sem varrer o
          país. Mudanças nas palavras de busca valem na próxima busca, não na que
          está rodando.
        </Nota>
        <Nota>
          A <b>simulação</b> percorre o portal inteiro e não guarda nada — mede
          quanto as palavras de busca trariam, antes de mexer na base; ela entra
          no histórico com 0 novas. <b>Continuar a interrompida</b> retoma a
          última busca incompleta do portal e soma os números. Só o PNCP sabe
          continuar, e nunca junto com o limite ou a simulação.
        </Nota>
        <Nota>
          <b>Incompleta não é falha</b>: a busca terminou, mas viu menos do que
          o portal tinha. O motivo aparece na linha dela — páginas que não
          carregaram, campos que vieram vazios ou o portal que mudou o formato da
          lista. Uma incompleta sem motivo nenhum foi interrompida à mão.
        </Nota>
      </Dobra>

      <Secao titulo="Últimas buscas" conta={rodadas.data?.length}>
        {rodadas.isPending && <Aviso simples>Lendo o histórico…</Aviso>}
        {rodadas.error && <Aviso tom="erro" simples>{(rodadas.error as Error).message}</Aviso>}
        {rodadas.data && <ListaDeRodadas linhas={rodadas.data} />}
      </Secao>
    </AreaConfig>
  );
}
